import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { ExperienceBadge } from '@/components/experience-badge';
import {
  AuthContainer,
  type DateItem,
  DatesStep,
  type WishlistItem,
  WishlistStep,
} from '@/components/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { usePreRegistration } from '@/hooks/mutations/use-pre-registration';

import { NameStep } from './-components/name-step';
import { StepIndicator } from './-components/step-indicator';

const emailSchema = z.object({
  email: z.string().email('E-mail inválido'),
});

type EmailFormValues = z.infer<typeof emailSchema>;

export const Route = createFileRoute('/registration/')({
  component: PreRegistrationPage,
});

const STORAGE_KEY = 'eumimo:registration';

interface FormData {
  name: string;
  dates: DateItem[];
  wishlistItems: WishlistItem[];
  email: string;
}

interface StoredData {
  step: number;
  formData: FormData;
}

function loadFromStorage(): StoredData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as StoredData;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function saveToStorage(step: number, formData: FormData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, formData }));
  } catch {
    // Ignore storage errors
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

function PreRegistrationPage() {
  const navigate = useNavigate();
  const preRegistrationMutation = usePreRegistration();

  const [step, setStep] = useState(() => {
    const stored = loadFromStorage();
    // Limit to 3 steps now
    const storedStep = stored?.step ?? 1;
    return storedStep > 3 ? 3 : storedStep;
  });
  const [formData, setFormData] = useState<FormData>(() => {
    const stored = loadFromStorage();
    return (
      stored?.formData ?? {
        name: '',
        dates: [],
        wishlistItems: [],
        email: '',
      }
    );
  });
  const [isEmailSheetOpen, setIsEmailSheetOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: formData.email },
  });

  useEffect(() => {
    saveToStorage(step, formData);
  }, [step, formData]);

  const handleNameNext = (name: string) => {
    setFormData({ ...formData, name });
    setStep(2);
  };

  const handleDatesNext = (dates: DateItem[]) => {
    setFormData({ ...formData, dates });
    setStep(3);
  };

  const handleWishlistNext = (wishlistItems: WishlistItem[]) => {
    setFormData({ ...formData, wishlistItems });
    setIsEmailSheetOpen(true);
  };

  const handleEmailSubmit = async (data: EmailFormValues) => {
    try {
      await preRegistrationMutation.mutateAsync({
        email: data.email,
        name: formData.name,
        dates: formData.dates,
        wishlist_items:
          formData.wishlistItems.length > 0
            ? formData.wishlistItems
            : undefined,
      });
      clearStorage();
      navigate({ to: '/registration/success' });
    } catch (error) {
      if (error instanceof HTTPError && error.response.status === 409) {
        toast.error('Este e-mail já está cadastrado.');
      } else {
        toast.error('Erro ao criar conta. Tente novamente.');
      }
    }
  };

  return (
    <AuthContainer>
      <StepIndicator currentStep={step} totalSteps={3} />

      {step === 1 && (
        <NameStep defaultValue={formData.name} onNext={handleNameNext} />
      )}

      {step === 2 && (
        <DatesStep
          defaultValue={formData.dates}
          onNext={handleDatesNext}
          onBack={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <WishlistStep
          defaultValue={formData.wishlistItems}
          continueLabel="Finalizar"
          skipLabel="Finalizar (pular)"
          onNext={handleWishlistNext}
          onBack={() => setStep(2)}
        />
      )}

      <p className="text-muted-foreground text-center text-sm">
        Já tem uma conta?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Entrar
        </Link>
      </p>

      <Sheet open={isEmailSheetOpen} onOpenChange={setIsEmailSheetOpen}>
        <SheetContent
          side="bottom"
          className="h-auto max-h-[90vh] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>Quase pronto!</SheetTitle>
            <SheetDescription>
              Informe seu e-mail para receber o link de acesso.
            </SheetDescription>
          </SheetHeader>
          <form
            onSubmit={handleSubmit(handleEmailSubmit)}
            className="mt-6 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                disabled={preRegistrationMutation.isPending}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-destructive text-sm">
                  {errors.email.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={preRegistrationMutation.isPending}
            >
              {preRegistrationMutation.isPending
                ? 'Enviando...'
                : 'Criar conta'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      <div className="flex flex-col space-y-2 text-center">
        <Link
          to="/"
          className="text-muted-foreground hover:text-primary text-sm"
        >
          Voltar para a página inicial
        </Link>
        <ExperienceBadge />
      </div>
    </AuthContainer>
  );
}
